import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Color, CommonProperties, CommonStyles as CS, CommonStyles } from '../../styles';
import Icon from '../Icon';
import { scale } from '../../utils/scale.utils';
import BaseText from '../BaseText';
import { cardStyle, XSmallText } from './card.utils';
import { policyMap } from '../../constants/PolicyMap';
import { useTranslation } from 'react-i18next';
import RowView from '../RowView';
import { Image } from 'expo-image';

const PriceSection = ({ price, labelDown, labelUp, labelUpStrike, policy, showPolicyName }) => {
  const { t } = useTranslation();

  return (
    <View>
      {policyMap[policy] && (
        <View
          style={[
            styles.policyHeader,
            policyMap[policy] && { backgroundColor: policyMap[policy].color },
            showPolicyName && CS.py0,
          ]}
        >
          {showPolicyName && (
            <BaseText style={styles.policyHeaderText}>{t(policyMap[policy].display)}</BaseText>
          )}
        </View>
      )}
      <View style={styles.priceSection}>
        {!!labelUp && (
          <BaseText style={[CS.small, CS.capitalize, labelUpStrike && styles.lineThrough]}>
            {labelUp}
          </BaseText>
        )}
        <BaseText style={cardStyle.large}>{price}</BaseText>
        {!!labelDown && <BaseText style={[CS.small, CS.mt1]}>{labelDown}</BaseText>}
      </View>
    </View>
  );
};

const Card = ({
  title,
  titleExtra,
  childrenTitle,
  subtitle,
  uppercaseSubtitle,
  value,
  textUpValue,
  textDownValue,
  icon,
  textUpIcon,
  textDownIcon,
  imageDownIcon,
  policy,
  children,
  image,
  renderLeft,
  footer,
  detail,
  visibleDetails,
  disableDetails,
  hidePrice,
  hideIcon,
  onPress,
  showPolicyName,
  disabled,
  expandInsteadOfPress,
  textUpStrike,
  alignCenterContent,
  isHotelRoomCard = false,
  isAirCard = false,
  ...rest
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const onPressCard = () => {
    setShowDetails(!showDetails);
  };
  const onPressPrice = (e) => {
    if (onPress) {
      e.preventDefault();
      if (!disabled) {
        if (expandInsteadOfPress) {
          setShowDetails(!showDetails);
        } else {
          onPress();
        }
      }
    }
  };
  return (
    <View {...rest}>
      <TouchableOpacity
        style={[CS.row, CS.pl3, CS.pb2, disabled && cardStyle.disabled]}
        onPress={isHotelRoomCard || isAirCard ? onPressCard : onPressPrice}
      >
        {image && <Image style={styles.leftImage} source={image} />}
        {renderLeft}
        {!hideIcon && (
          <View style={[CS.justifyCenter, styles.containerView]}>
            {!disableDetails && !visibleDetails && (
              <TouchableOpacity
                style={styles.containerView}
                onPress={() => setShowDetails(!showDetails)}
              >
                <Icon name={showDetails ? 'minus' : 'plus'} style={styles.plus} />
              </TouchableOpacity>
            )}
            <View style={styles.vendorWrap}>
              {textUpIcon && <XSmallText style={cardStyle.grayColor}>{textUpIcon}</XSmallText>}
              {icon && <Image style={styles.vendor} source={icon} />}
              {textDownIcon && <XSmallText style={cardStyle.grayColor}>{textDownIcon}</XSmallText>}
              {imageDownIcon && <Image style={styles.carVendor} source={imageDownIcon} />}
            </View>
          </View>
        )}
        <View style={styles.composedRow}>
          <View style={[CS.shrink, alignCenterContent && styles.alignCenterParent]}>
            <RowView>
              <BaseText style={[cardStyle.medium, CS.wrap]} numberOfLines={1} ellipsizeMode="tail">
                {title}
              </BaseText>
              {titleExtra ? <BaseText style={cardStyle.titleExtra}>{titleExtra}</BaseText> : null}
            </RowView>
            {childrenTitle}
            <BaseText
              style={[
                CommonStyles.small,
                cardStyle.grayColor,
                CS.mt2,
                uppercaseSubtitle && CS.uppercase,
              ]}
            >
              {subtitle}
            </BaseText>
          </View>
          <View style={alignCenterContent && styles.alignCenterChild}>{children}</View>
          <View />
        </View>
        {!hidePrice && (
          <TouchableOpacity onPress={onPressPrice}>
            <PriceSection
              showPolicyName={showPolicyName}
              policy={policy}
              price={value}
              labelUpStrike={textUpStrike}
              labelDown={textDownValue}
              labelUp={textUpValue}
            />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
      {(showDetails || visibleDetails) && !disableDetails ? detail : null}
      {footer}
    </View>
  );
};

const styles = StyleSheet.create({
  plus: {
    fontSize: scale(8 * 0.8),
    color: Color.gray,
    alignSelf: 'center',
    ...CommonProperties.mr2,
  },
  composedRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...CommonProperties.py3,
    ...CommonProperties.pl3,
    ...CommonProperties.pr2,
  },
  leftImage: {
    height: scale(75),
    width: scale(116 * 0.8),
    resizeMode: 'cover',
    borderTopLeftRadius: scale(6),
    borderBottomLeftRadius: scale(6),
  },
  vendor: {
    width: scale(42 * 0.8),
    height: scale(42 * 0.8),
    resizeMode: 'contain',
  },
  carVendor: {
    width: scale(30),
    height: scale(10),
    resizeMode: 'contain',
  },
  vendorWrap: {
    ...CommonProperties.mt2,
    ...CommonProperties.centerAlignment,
  },
  priceSection: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
    ...CommonProperties.py2,
    ...CommonProperties.pr3,
  },
  policyHeader: {
    width: '100%',
    ...CommonProperties.px3,
    ...CommonProperties.py1,
  },
  policyHeaderText: {
    color: 'white',
    textAlign: 'center',
    ...CommonProperties.small,
  },
  lineThrough: {
    textDecorationLine: 'line-through',
    color: Color.text.low,
  },
  alignCenterParent: {
    flex: 65,
  },
  alignCenterChild: {
    flex: 35,
  },
  containerView: {
    flexDirection: 'row',
  },
});

export default Card;
